import { Injectable } from '@angular/core';
import { tryParseInt } from '@helpers/tryParse';

@Injectable({
  providedIn: 'root',
})
export class IdValidationService {
  constructor() {}

  // going to accept ID as string to account for ID starting with 0
  public isValid(id: string): boolean {
    // make sure the string passed as an ID can be parsed as a number
    if (tryParseInt(id) === null) {
      return false;
    }

    // get the chars of the id as an array
    // make sure length is equal to 13
    const idArray = this.toNumberArray(id);
    if (idArray.length !== 13) {
      return false;
    }

    // Make sure the DOB portion of the ID is a valid DOB
    if (!this.isValidDOB(id)) {
      return false;
    }

    // validate ID using Luhn algorithm check ID
    return this.validateId(idArray);
  }

  private isValidDOB(id: string): boolean {
    const month = tryParseInt(id.substring(2, 4));
    const day = tryParseInt(id.substring(4, 6));

    // Validate month and day by making sure they're both greater than 0 and less than 13 and 32 respectively
    if (
      month < 1 ||
      month > 12 ||
      month == null ||
      day < 1 ||
      day > 31 ||
      day == null
    ) {
      return false;
    }

    //  Apr, Jun, Sep, Nov must not have more than 30 days
    if (
      (month === 4 || month === 6 || month === 9 || month === 11) &&
      day > 30
    ) {
      return false;
    }

    // Feb must not have more than 29 days
    if (month === 2 && day > 29) {
      return false;
    }

    // If Feb and day is 29, make sure is leap year
    if (month === 2 && day === 29) {
      let year = tryParseInt(id.substring(0, 2));
      const currentYearTwoDigit = new Date().getFullYear() % 100;
      year += year > currentYearTwoDigit ? 1900 : 2000;

      // Check if is leap year
      if (year % 400 === 0 || (year % 4 === 0 && year % 100 !== 0)) {
        return true;
      }

      return false;
    }

    return true;
  }

  private toNumberArray(id: string): number[] {
    return id.split('').map((num) => Number(num));
  }

  private validateId(idArray: number[]): boolean {
    // get check digit (the last digit of id)
    const checkDigit = idArray[12];
    // get a list of the odd values except the check digit
    const oddValueArray = this.getOddValueArray(idArray);
    // add the odd values together
    const oddValueSum = this.getSumOfNumberArray(oddValueArray);
    // get a list of the even values
    const evenValueArray = this.getEvenValueArray(idArray);
    // concat even values into one number IE ([2,8,4] = 284)
    let concatedEvenValues = this.concatNumberArray(evenValueArray);
    // multiply the concated even numbers value by 2
    concatedEvenValues = concatedEvenValues * 2;
    // get a list of values in new concated even number's value
    const concatedEvenValuesArray = this.toNumberArray(
      String(concatedEvenValues)
    );
    // add the list of concated even number values together
    const sumOfConcatedEvenValues = this.getSumOfNumberArray(
      concatedEvenValuesArray
    );
    // add the odd value sum and the concated even number sum together
    const sumofOddAndEven = oddValueSum + sumOfConcatedEvenValues;
    // convert the above sum into a list of values
    const sumOfOddAndEvenArray = this.toNumberArray(String(sumofOddAndEven));
    // get the last digit of above value
    const lastDigit = sumOfOddAndEvenArray[sumOfOddAndEvenArray.length - 1];
    // calculate the verifyCheckDigit which is 10 minus the above value.
    // (Using '%10' to ensure that 10-0 still equals 0, because if 10-0 is
    // left to equal 10 as per normal behaviour, the compare digits will return false).
    let verifyCheckDigit = (10 - lastDigit) % 10;
    // if the verifyCheckDigit has two digits, get the last digit
    if (verifyCheckDigit > 9) {
      verifyCheckDigit = this.toNumberArray(String(verifyCheckDigit)).pop();
    }
    // compare the check digits and return true or false
    // if they are the same, then this is a valid ID
    return this.compareCheckDigits(checkDigit, verifyCheckDigit);
  }

  private getSumOfNumberArray(numberArray: number[]): number {
    let sum = 0;
    for (let i = 0; i < numberArray.length; i++) {
      sum = sum + numberArray[i];
    }
    return sum;
  }

  private getOddValueArray(idArray: number[]): number[] {
    const oddArray = [];
    oddArray.push(idArray[0]);
    oddArray.push(idArray[2]);
    oddArray.push(idArray[4]);
    oddArray.push(idArray[6]);
    oddArray.push(idArray[8]);
    oddArray.push(idArray[10]);
    return oddArray;
  }

  private getEvenValueArray(idArray: number[]): number[] {
    const evenArray = [];
    evenArray.push(idArray[1]);
    evenArray.push(idArray[3]);
    evenArray.push(idArray[5]);
    evenArray.push(idArray[7]);
    evenArray.push(idArray[9]);
    evenArray.push(idArray[11]);
    return evenArray;
  }

  private concatNumberArray(numberArray: number[]): number {
    return Number(numberArray.join(''));
  }

  private compareCheckDigits(
    checkDigit: number,
    verifyCheckDigit: number
  ): boolean {
    if (verifyCheckDigit === checkDigit) {
      return true;
    }
    return false;
  }
}
